import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { apps } from "@/lib/apps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const exportCsv = (slug, rows) => {
  const head = "email,language,confirmed,unsubscribed,joined,notified";
  const lines = rows.map((r) => [r.email, r.lang, r.confirmed, r.unsubscribed, r.created_at, r.notified_at || ""].join(","));
  const url = URL.createObjectURL(new Blob([[head, ...lines].join("\n")], { type: "text/csv" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: `${slug}-waitlist.csv` });
  a.click();
  URL.revokeObjectURL(url);
};

const AppWaitlist = ({ app, rows, onNotify, busy }) => {
  const confirmed = rows.filter((r) => r.confirmed && !r.unsubscribed);
  const toNotify = confirmed.filter((r) => !r.notified_at).length;
  return (
    <div data-testid={`admin-waitlist-${app.slug}`} className="glass rounded-3xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src={app.logo} alt="" className="h-12 w-12 rounded-2xl object-cover" style={{ transform: `scale(${app.logoScale})` }} />
          <div>
            <p className="font-display text-xl font-semibold">{app.name}</p>
            <p data-testid={`admin-waitlist-count-${app.slug}`} className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">
              {confirmed.length} confirmed · {rows.length - confirmed.length} pending/unsubscribed · {toNotify} to notify
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button type="button" data-testid={`admin-waitlist-export-${app.slug}`} disabled={!rows.length} onClick={() => exportCsv(app.slug, rows)} className="btn-ghost !py-2.5 disabled:opacity-40">
            <Download className="h-4 w-4" /> CSV
          </button>
          <button type="button" data-testid={`admin-waitlist-notify-${app.slug}`} disabled={!toNotify || busy} onClick={() => onNotify(app, toNotify)} className="btn-primary !py-2.5 disabled:opacity-40">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Notify waitlist ({toNotify})
          </button>
        </div>
      </div>
      {rows.length > 0 && (
        <div className="mt-5 max-h-64 overflow-auto rounded-2xl border border-white/10" data-lenis-prevent>
          {rows.map((r) => (
            <div key={r.id} className="flex flex-wrap justify-between gap-2 border-b border-white/5 px-4 py-2.5 text-sm last:border-0">
              <span className="text-white/80">{r.email}</span>
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/40">
                {r.lang} · {r.unsubscribed ? "unsubscribed" : r.confirmed ? (r.notified_at ? "notified" : "confirmed") : "unconfirmed"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const AdminWaitlist = ({ adminKey }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const headers = { "X-Admin-Key": adminKey };

  const load = () => axios.get(`${API}/admin/waitlist`, { headers }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const notify = async (app, count) => {
    if (!window.confirm(`Send the launch e-mail for ${app.name} to ${count} confirmed subscriber(s)? Only do this once the app is live.`)) return;
    setBusy(app.slug);
    try {
      const { data } = await axios.post(`${API}/admin/waitlist/${app.slug}/notify`, {}, { headers });
      toast.success(`Sent ${data.sent} launch e-mail(s)${data.failed ? `, ${data.failed} failed` : ""}.`);
      await load();
    } catch {
      toast.error("Could not send launch e-mails.");
    } finally {
      setBusy("");
    }
  };

  if (!rows) return <Loader2 className="h-5 w-5 animate-spin text-white/50" />;
  return (
    <div data-testid="admin-waitlist" className="space-y-4">
      {apps.map((app) => (
        <AppWaitlist key={app.slug} app={app} rows={rows.filter((r) => r.app === app.slug)} onNotify={notify} busy={busy === app.slug} />
      ))}
    </div>
  );
};
